export function Terms() {
  return (
    <article className="prose-thelive mt-6">
      <h2 className="font-display font-black text-3xl mb-2">terms of service</h2>
      <p className="text-ink-mute text-sm mb-8">last updated: 2026-05-04 · draft for legal review</p>

      <Section title="1. parties &amp; scope">
        <p>
          These terms govern your use of thelivephotos.com (the &ldquo;service&rdquo;), provided by{" "}
          <b>Terradynamic Ltd</b>, established in <b>Bulgaria</b>
          (&ldquo;we&rdquo;, &ldquo;us&rdquo;).
        </p>
      </Section>

      <Section title="2. eligibility">
        <p>You must be at least 18 years old. By using the service you confirm that you are.</p>
      </Section>

      <Section title="3. content you upload">
        <p>You retain ownership of the photos you upload. You grant us a limited, revocable, non-exclusive licence
        to store and process them solely to provide the service to you (e.g. moderation, animation, delivery to your
        own &ldquo;my live photos&rdquo;).</p>
        <p className="font-medium">You must not upload:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>any image of a person under 18, in any context</li>
          <li>sexual, sexually-suggestive, or otherwise explicit content</li>
          <li>images of any real person without their explicit consent</li>
          <li>copyrighted material you do not have the right to use</li>
          <li>racist content, racial slurs, ethnic stereotyping, Nazi or supremacist imagery,
              or anything that demeans or incites against people on the basis of race,
              ethnicity, national origin, religion, caste, disability, gender, sexual
              orientation, or age</li>
          <li>content that depicts real-world violence, hate, terrorism, or harassment</li>
          <li>anything that is illegal in either your jurisdiction or in <b>Bulgaria</b></li>
        </ul>
        <p>
          Submissions are screened automatically before rendering. We may reject, delete, or report material at our
          discretion. You may request a human review of a moderation decision via the
          {" "}<a className="text-coral-dark underline" href="/report">report page</a>{" "}
          or by writing to abuse@thelivephotos.com.
        </p>
      </Section>

      <Section title="4. tokens, payments, EU consumer right of withdrawal">
        <p>
          Free credits are granted on signup as a courtesy and may change without notice.
        </p>
        <p>
          Tokens are <b>digital content supplied immediately upon purchase</b> at your express request.
          As permitted under Article 16(m) of the Consumer Rights Directive (EU) 2011/83 and equivalent UK
          Consumer Contracts Regulations 2013, you <b>expressly consent</b> at checkout to immediate performance
          and acknowledge that you thereby <b>lose the 14-day right of withdrawal</b> for tokens that have been
          made available for use. Unused tokens may be refunded at our discretion if the service is materially
          broken; otherwise tokens are non-refundable.
        </p>
        <p>Tokens have no cash value and do not currently expire.</p>
        <p>
          Prices include applicable VAT for EU/UK consumers, calculated by Stripe based on your billing address.
          Receipts are sent by email and available in your Stripe customer portal.
        </p>
      </Section>

      <Section title="5. acceptable use">
        <p>You agree not to (a) attempt to bypass moderation, (b) use the service to impersonate or defame
        others, (c) reverse-engineer, scrape, or overload the service, (d) resell access without our written
        permission, or (e) submit content that infringes any third-party right.</p>
      </Section>

      <Section title="6. termination">
        <p>We may suspend or terminate your account at any time if you violate these terms or applicable law.
        You may delete your account at any time; on deletion we erase your jobs and uploaded files within 30 days,
        subject to legal retention requirements (see <a className="text-coral-dark underline" href="/privacy">privacy notice</a>).</p>
      </Section>

      <Section title="7. liability">
        <p>
          The service is provided &ldquo;as is&rdquo; without warranty beyond that required by law. Nothing in
          these terms limits or excludes our liability for: (a) death or personal injury caused by negligence;
          (b) fraud or fraudulent misrepresentation; (c) any matter for which it would be unlawful to limit
          liability — including, for EU consumers, statutory rights under Directive 2019/770 (digital content)
          and Directive 2019/771 (sale of goods), and for UK consumers, statutory rights under the Consumer
          Rights Act 2015.
        </p>
        <p>
          To the fullest extent permitted by law, our aggregate liability under these terms is limited to the
          greater of (i) what you paid us in the 12 months before the claim, or (ii) €100.
        </p>
      </Section>

      <Section title="8. governing law &amp; jurisdiction">
        <p>
          These terms are governed by the laws of <b>Bulgaria</b> without regard to its
          conflict-of-law rules. Disputes are subject to the exclusive jurisdiction of the courts of{" "}
          <b>Sofia, Bulgaria</b>, except where mandatory consumer-protection rules of your
          country of residence grant you the right to bring proceedings in your local courts.
        </p>
        <p>
          EU consumers may also use the European Commission&rsquo;s online dispute resolution platform
          at <a className="text-coral-dark underline" href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>.
        </p>
      </Section>

      <Section title="9. changes">
        <p>
          We may update these terms; material changes will be announced by email and you will be required to
          re-accept them before submitting further renders.
        </p>
      </Section>

      <Section title="10. contact">
        <p>
          For questions: <a className="text-coral-dark underline" href="mailto:hello@thelivephotos.com">hello@thelivephotos.com</a>.
          For privacy / data-protection requests: <a className="text-coral-dark underline" href="mailto:privacy@thelivephotos.com">privacy@thelivephotos.com</a>.
          For abuse / illegal-content reports: <a className="text-coral-dark underline" href="/report">report page</a>{" "}
          or abuse@thelivephotos.com.
        </p>
      </Section>

      <p className="text-xs text-ink-mute mt-10">
        This is a draft for product review and does not constitute legal advice. Have a qualified lawyer in
        your jurisdiction (and in the UK, if you target UK users) review before launch.
      </p>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-7">
      <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
      <div className="space-y-2 text-ink-soft">{children}</div>
    </section>
  );
}
